/**
 * Стабильный бюджет: экран включения и экран управления.
 *
 * Весь смысл режима человеку надо объяснить без единого финансового слова:
 * «деньги приходят рывками — давай платить себе понемногу и ровно». Поэтому
 * здесь нигде нет ни «сглаживания денежного потока», ни «аннуитета», ни
 * процентов; есть рубли, даты и один вопрос: на сколько хватит.
 *
 * Два правила экрана, и оба стоят человеку денег, если их нарушить:
 *
 * · **выплата себе называется своими словами.** «До выплаты 12 дней» рядом с
 *   «Доступно» человек прочтёт как ожидаемый заработок, если не
 *   сказать прямо: это твои деньги из буфера;
 * · **ничего не происходит молча.** Сумма не уменьшается сама, буфер не
 *   доливается из подушки, режим не включается за человека, и любое
 *   изменение показывается парой «было → станет» с новой датой исчерпания.
 */
import { useState, type ReactNode } from 'react';
import {
  ZERO, add, bufferOf, cadenceTitle, clampPos, emergencyFund, formatMoney, previewChange,
  needsDecision, proposeAllocation, splitArrival, stableOutlook, stableReadiness, sub,
  sustainablePayout,
  type Money, type PayoutCadence, type PayoutState, type StableBudgetPlan, type StableGap,
  type StableOutlook,
} from '@skoka/core';
import { SheetWrap } from './sheet.js';
import { Tip } from './tip.js';
import { flash } from './flash.js';
import { AmountInput } from './amount.js';
import { AccountField } from './operation-fields.js';
import { dayMonthYear, plural } from './text.js';
import {
  createStablePlan, formatAmountInput, moveFromEmergencyFund, parseAmountInput, payoutAllowed,
  payoutSelf, pauseStablePlan, planInput, reconcileStable, resumeStablePlan, skipPayout,
  stableReadinessOf, stableSnapshot, stopStablePlan, takeFromBuffer, topUpBuffer,
  updateStablePlan,
  type AppState,
} from './store.js';
import { DateButton } from './date-picker.js';

const CADENCES: PayoutCadence[] = ['weekly', 'biweekly', 'monthly'];

/** Состояние наступления человеческими словами. */
const PAYOUT_STATE: Record<PayoutState, string> = {
  upcoming: 'впереди',
  due_today: 'сегодня',
  overdue: 'не проведена',
  completed: 'выплачено',
  skipped: 'пропущена',
  unfunded: 'не обеспечена — денег в запасе не хватит',
};

/** Выплаты себе как внешние приходы — для осторожной оценки по истории. */
const arrivalsOf = (state: AppState) => state.transactions
  .filter((t) => t.type === 'income' && t.link?.kind === 'income')
  .map((t) => ({
    date: t.occurredDate,
    amount: t.amount,
    ...(t.link?.kind === 'income' && t.link.incomeStreamId
      ? { streamId: t.link.incomeStreamId }
      : {}),
    kind: (t.link?.kind === 'income' && t.link.incomeKind) || 'oneoff' as const,
  }));

export function StableSheet({ state, onClose, onSaved, onAllocate }: {
  state: AppState;
  onClose: () => void;
  onSaved: () => Promise<void> | void;
  /** Деньги стали свободными — предложить новую раскладку. */
  onAllocate: () => void;
}) {
  const snapshot = stableSnapshot(state);
  return snapshot
    ? <Manage state={state} onClose={onClose} onSaved={onSaved} onAllocate={onAllocate} />
    : <Setup state={state} onClose={onClose} onSaved={onSaved} />;
}

// ──────────────────────────────────────────────────────────────── включение

/**
 * Первый заход: объяснение, счёт, сумма, ритм и **два сценария** до согласия.
 *
 * Сценарии обязательны. Без них человек соглашается на цифру, не зная, чем
 * она кончится: «платить себе 60 000» звучит одинаково и при буфере на
 * полгода, и при буфере на полтора месяца.
 */
function Setup({ state, onClose, onSaved }: {
  state: AppState; onClose: () => void; onSaved: () => Promise<void> | void;
}) {
  const spendable = state.accounts.filter((a) => !a.archived && a.kind !== 'credit_card');
  const [step, setStep] = useState<'why' | 'money' | 'rhythm' | 'preview'>('why');
  const [accountId, setAccountId] = useState(spendable.find((a) => a.isLiquid)?.id ?? spendable[0]?.id ?? '');
  const [toAccountId, setTo] = useState(accountId);
  const [buffer, setBuffer] = useState('');
  const [cadence, setCadence] = useState<PayoutCadence>('biweekly');
  const [payout, setPayout] = useState('');
  const [first, setFirst] = useState(state.today as string);
  const [busy, setBusy] = useState(false);

  const bufferSum = parseAmountInput(buffer);
  const payoutSum = parseAmountInput(payout);
  const readiness = stableReadinessOf(state, { have: bufferSum });
  const guess = sustainablePayout(arrivalsOf(state), cadence);

  const draft: StableBudgetPlan = {
    id: 'draft',
    accountId,
    toAccountId,
    cadence,
    payout: payoutSum,
    firstPayoutDate: first as StableBudgetPlan['firstPayoutDate'],
    status: 'active',
    createdAt: new Date().toISOString(),
  };
  const outlook = stableOutlook({
    today: state.today, plan: draft, buffer: bufferSum, payouts: [],
  });

  // Ближайшие обязательные платежи до первой выплаты: если буфер съест
  // деньги, которыми человек собирался их закрыть, сказать надо сейчас.
  const proposal = proposeAllocation(planInput(state));
  const free = clampPos(sub(proposal.liquid, proposal.reserved));
  const tooMuch = bufferSum > free;

  const [problem, setProblem] = useState('');
  const save = async () => {
    setBusy(true);
    const r = await createStablePlan({
      accountId,
      toAccountId,
      buffer: bufferSum,
      cadence,
      payout: payoutSum,
      firstPayoutDate: first as StableBudgetPlan['firstPayoutDate'],
    });
    setBusy(false);
    if (!r.plan) { setProblem(r.reason ?? 'Не вышло'); return; }
    // Подтверждение после действия (04.11.2026): владелец не понимал, что произошло.
    flash('Стабильный бюджет включён');
    await onSaved();
  };

  /*
   * Три шага с номером и «назад» на каждом: раньше вернуться можно было
   * только с последнего, а сколько шагов впереди — не говорилось. Всё
   * введённое живёт здесь, в `Setup`, поэтому назад ничего не теряет.
   */
  const stepNo = step === 'money' ? 1 : step === 'rhythm' ? 2 : step === 'preview' ? 3 : 0;
  const back = step === 'money' ? 'why' as const : step === 'rhythm' ? 'money' as const
    : step === 'preview' ? 'rhythm' as const : null;

  return (
    <SheetWrap title={stepNo ? `Стабильный бюджет · шаг ${stepNo} из 3` : 'Стабильный бюджет'}
      onClose={onClose} {...(back ? { onBack: () => setStep(back), backLabel: 'Назад к предыдущему шагу' } : {})}>
      {step === 'why' && (
        <>
          <div className="note" style={{ marginTop: 0 }}>
            Деньги приходят когда как: то пусто, то сразу за два проекта. Жить
            по такому графику тяжело — не потому, что денег мало, а потому что
            непонятно, сколько можно тратить сегодня.
          </div>
          <div className="note">
            Можно иначе. Откладываем часть денег в отдельный запас и платим
            себе из него понемногу и по расписанию — как зарплату. Бюджет
            перестаёт зависеть от того, когда заплатит заказчик.
          </div>
          <div className="note">
            Это не новые деньги и не доход: ты просто распоряжаешься своими.
            В «Фактически пришло» такие выплаты не попадают.
          </div>
          <button className="btn pri" onClick={() => setStep('money')}>Дальше</button>
        </>
      )}

      {step === 'money' && (
        <div className="form">
          {/* Счета — тем же списком с остатками, что и везде: две одинаковые
              группы чипов без сумм не помогали выбрать. */}
          <div className="oprows">
            <AccountField label="Где держим запас" pickerTitle="Где держим запас" accounts={spendable}
              value={accountId}
              onChange={(id) => { setAccountId(id); if (toAccountId === accountId) setTo(id); }} />
          </div>
          <label>Сколько отложить в запас
            <AmountInput value={buffer} onChange={setBuffer} placeholder="60 000" /></label>

          {bufferSum > ZERO && (
            <div className={`note${readiness.ready ? '' : ' warn'}`}>
              {readiness.unknowns.length > 0
                ? readiness.unknowns[0]
                : readiness.ready
                  ? `Хватает: обязательные платежи и жизнь на два месяца — ${formatMoney(readiness.required)}.`
                  : `Для стабильного бюджета пока не хватает ${formatMoney(readiness.missing)}. `
                    + 'Сначала соберём запас на два месяца — платежи и жизнь.'}
            </div>
          )}
          {tooMuch && (
            <div className="note warn">
              Свободных денег {formatMoney(free)} — меньше, чем ты откладываешь.
              Остальное сейчас закреплено под платежи и цели.
            </div>
          )}
          <div className="oprows">
            <AccountField label="Куда платить себе" pickerTitle="Куда платить себе" accounts={spendable}
              value={toAccountId} onChange={setTo} />
          </div>
          {toAccountId !== accountId && (
            <div className="note">
              Счета разные — значит каждая выплата будет настоящим переводом,
              и подтверждать его будешь ты. Приложение деньги не двигает.
            </div>
          )}
          <button className="btn pri" disabled={bufferSum <= ZERO || !accountId}
            onClick={() => setStep('rhythm')}>Дальше</button>
        </div>
      )}

      {step === 'rhythm' && (
        <div className="form">
          <div className="pick"><div className="k">Как часто платить себе</div>
            <div className="chips">
              {CADENCES.map((c) => (
                <button key={c} className={`chip${cadence === c ? ' on' : ''}`}
                  onClick={() => setCadence(c)}>{cadenceTitle(c)}</button>
              ))}
            </div>
          </div>
          <label>Сколько за раз
            {guess.payout === null && (
              <Tip>Пока назови сумму сам: приложение не станет называть выплату
                устойчивой, не имея на это оснований.</Tip>
            )}
            <AmountInput value={payout} onChange={setPayout} placeholder="30 000" /></label>
          {guess.payout !== null ? (
            <button className="btn inline"
              onClick={() => setPayout(formatAmountInput(String(guess.payout! / 100)))}>
              Осторожно по твоей истории — {formatMoney(guess.payout)}
            </button>
          ) : (
            <div className="note">{guess.why}.</div>
          )}
          <label>Первая выплата
            <DateButton label="Первая выплата" value={first} onChange={setFirst} /></label>
          <button className="btn pri" disabled={payoutSum <= ZERO || !first}
            onClick={() => setStep('preview')}>Посмотреть, что будет</button>
        </div>
      )}

      {step === 'preview' && (
        <>
          <div className="blk">
            <div className="blkhead"><b>Если новых доходов не будет</b></div>
            <Rows rows={[
              ['В запасе', formatMoney(bufferSum)],
              ['Выплата', `${formatMoney(payoutSum)} · ${cadenceTitle(cadence)}`],
              ['Хватит выплат', String(outlook.payoutsLeft)],
              ['Запас кончится', outlook.exhaustsOn ? dayMonthYear(outlook.exhaustsOn) : '—'],
            ]} />
            <div className="note" style={{ marginTop: 0 }}>{outlook.why}</div>
          </div>

          <div className="blk">
            <div className="blkhead"><b>Если доходы будут как обычно</b></div>
            {guess.payout === null ? (
              <div className="note" style={{ marginTop: 0 }}>
                {guess.why}. Этот сценарий посчитать не из чего — и выдумывать
                его я не стану.
              </div>
            ) : (
              <div className="note" style={{ marginTop: 0 }}>
                По слабым месяцам твоей истории выходит около {formatMoney(guess.payout)}
                {' '}за выплату. {payoutSum > guess.payout
                  ? 'Ты берёшь больше — значит запас будет таять.'
                  : 'Выбранная сумма в это укладывается.'}
              </div>
            )}
          </div>

          <ObligationsAhead state={state} until={outlook.exhaustsOn} />

          {problem && <div className="note warn">{problem}</div>}
          <button className="btn pri" disabled={busy} onClick={save}>
            {busy ? 'Включаю…' : 'Включить стабильный бюджет'}
          </button>
          <button className="btn" onClick={() => setStep('rhythm')}>Изменить суммы</button>
        </>
      )}
    </SheetWrap>
  );
}

/**
 * Пришли внешние деньги: куда их разложить.
 *
 * Предложение, а не действие. До нажатия в буфер не уходит ни рубля, и
 * сохранённого правила «всегда откладывать 70 %» тоже нет: решение о своих
 * деньгах человек принимает каждый раз, пока сам не попросит иначе.
 *
 * Порядок предложения не случаен: сначала непокрытые обязательные платежи —
 * иначе запас наполнялся бы за счёт просрочки, — потом буфер до
 * двухмесячного минимума, остальное остаётся нераспределённым.
 */
export function ArrivalSheet({ state, amount, onClose, onDone }: {
  state: AppState;
  amount: Money;
  onClose: () => void;
  onDone: () => Promise<void> | void;
}) {
  const snapshot = stableSnapshot(state);
  const proposal = proposeAllocation(planInput(state));
  const readiness = stableReadinessOf(state);
  const suggested = splitArrival({
    amount,
    // Непокрытый защищённый слой: платежи и жизнь до ближайшей точки.
    obligationsGap: proposal.shortfall,
    bufferGap: readiness.missing,
  });
  const [toBuffer, setToBuffer] = useState(
    formatAmountInput(String(suggested.toBuffer / 100)),
  );
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState('');
  if (!snapshot) return null;

  const bufferSum = parseAmountInput(toBuffer);
  const rest = clampPos(sub(amount, add(bufferSum, suggested.toObligations)));

  return (
    <SheetWrap title={`Пришло ${formatMoney(amount)}`} onClose={onClose}>
      <Rows rows={[
        ['В запас стабильного бюджета', formatMoney(bufferSum)],
        ['На ближайшие платежи', formatMoney(suggested.toObligations)],
        ['Пока не распределено', formatMoney(rest)],
      ]} />
      <div className="form">
        <label>Сколько отложить в запас
          <Tip>Период выплат этот приход не сдвигает: следующая выплата остаётся
            {' '}{dayMonthYear(snapshot.outlook.due?.scheduledDate ?? state.today)}.</Tip>
          <AmountInput value={toBuffer} onChange={setToBuffer} placeholder="0" /></label>
      </div>
      <div className="note">
        {readiness.missing > ZERO
          ? `До запаса на два месяца не хватает ${formatMoney(readiness.missing)}.`
          : 'Запаса на два месяца уже хватает — можно оставить деньги нераспределёнными.'}
      </div>
      {problem && <div className="note warn">{problem}</div>}
      <button className="btn pri" disabled={busy}
        onClick={async () => {
          setBusy(true);
          if (bufferSum > ZERO) {
            const result = await topUpBuffer(snapshot.plan.id, bufferSum);
            if (!result.done) {
              setProblem(result.reason ?? 'Не получилось пополнить запас');
              setBusy(false);
              return;
            }
            flash(`Запас пополнен на ${formatMoney(bufferSum)}`);
          }
          await onDone();
        }}>
        {busy ? 'Записываю…' : 'Разложить так'}
      </button>
      <button className="btn" onClick={onClose}>Оставить всё нераспределённым</button>
    </SheetWrap>
  );
}

// ─────────────────────────────────────────────────────────────── управление

function Manage({ state, onClose, onSaved, onAllocate }: {
  state: AppState;
  onClose: () => void;
  onSaved: () => Promise<void> | void;
  onAllocate: () => void;
}) {
  const snapshot = stableSnapshot(state);
  const [mode, setMode] = useState<
  'view' | 'change' | 'topup' | 'fund' | 'stop' | 'reconcile' | 'take'>('view');
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState('');

  if (!snapshot) return null;
  const { plan, buffer, outlook } = snapshot;
  const due = outlook.due;

  const allowed = due ? payoutAllowed(state, due.scheduledDate) : { ok: false };
  const waiting = needsDecision(due);

  const pay = async () => {
    if (!due) return;
    setBusy(true);
    const r = await payoutSelf({ planId: plan.id, scheduledDate: due.scheduledDate });
    setBusy(false);
    if (!r.done) { setProblem(r.reason ?? 'Не вышло'); return; }
    flash(`Выплата ${formatMoney(due.amount)} — себе`);
    await onSaved();
    onAllocate();
  };

  const skip = async () => {
    if (!due) return;
    setBusy(true);
    const r = await skipPayout({ planId: plan.id, scheduledDate: due.scheduledDate });
    setBusy(false);
    if (!r.done) { setProblem(r.reason ?? 'Не вышло'); return; }
    flash('Выплата пропущена');
    await onSaved();
  };

  return (
    <SheetWrap title="Стабильный бюджет" onClose={onClose}>
      {mode === 'view' && (
        <>
          <Rows rows={[
            ['В запасе', formatMoney(buffer)],
            ['Выплата', `${formatMoney(plan.payout)} · ${cadenceTitle(plan.cadence)}`,
              'Это твои деньги из буфера, а не ожидаемый внешний доход: в доходах '
                + 'месяца они не появятся.'],
            ['Следующая', due ? dayMonthYear(due.scheduledDate) : '—'],
            ['Хватит выплат', String(outlook.payoutsLeft)],
            ['Запас кончится', outlook.exhaustsOn ? dayMonthYear(outlook.exhaustsOn) : '—'],
          ]} />
          <div className="note" style={{ marginTop: 0 }}>{outlook.why}</div>

          {plan.status === 'paused' && (
            <div className="note warn">
              Режим приостановлен: новые выплаты не наступают и деньги из
              запаса не выдаются. Возобновишь — ближайшей станет первая дата
              начиная со дня возобновления, а не пропущенные за время паузы.
            </div>
          )}

          <Warning outlook={outlook} />

          {/*
            Долгое отсутствие разбирается одним решением, а не по выплате.

            Тринадцать вопросов подряд человек не осилит и ответит наугад —
            а наугад в деньгах хуже, чем «не знаю».
          */}
          {outlook.gap && (
            <section className="blk alarm">
              <div className="blkhead"><b>Режим не сверялся
                с {dayMonthYear(outlook.gap.from)}</b></div>
              <div className="note" style={{ marginTop: 0 }}>
                {plural(outlook.gap.count, 'выплата', 'выплаты', 'выплат')} без ответа
                {' '}на {formatMoney(outlook.gap.amount)} по нынешней сумме. Что происходило
                с выплатами всё это время?
              </div>
              <button className="btn" onClick={() => setMode('reconcile')}>Разобрать</button>
            </section>
          )}

          {/*
            Решение по сегодняшней и просроченной выплате — здесь.

            Пока его нет, следующая дата сроком не считается: перескочить
            через невзятые деньги значило бы обещать спокойный месяц тому,
            кто их не получил.
          */}
          {waiting && due && plan.status === 'active' && !outlook.gap && (
            <section className="blk alarm">
              <div className="blkhead"><b>{due.state === 'due_today'
                ? 'Сегодня выплата себе'
                : `Выплата за ${dayMonthYear(due.scheduledDate)} не проведена`}
                <Tip>Пока решения нет, следующий срок не наступает: я не знаю, взял
                  ты эти деньги или нет.</Tip></b></div>
              <div className="note" style={{ marginTop: 0 }}>
                {formatMoney(due.amount)} ждут решения.
              </div>
              <button className="btn" disabled={busy} onClick={skip}>
                В этот раз пропустить
              </button>
            </section>
          )}

          {due && allowed.ok && (
            <button className="btn pri" disabled={busy} onClick={pay}>
              {busy ? 'Записываю…' : `Выплатить себе ${formatMoney(due.amount)}`}
            </button>
          )}
          {problem && <div className="note warn">{problem}</div>}

          <div className="blk">
            <div className="blkhead"><b>Ближайшие выплаты</b></div>
            {outlook.schedule.filter((s) => !s.decided).slice(0, 4).map((s) => (
              <div className="li" key={s.id}>
                <div className="nm"><b>{dayMonthYear(s.scheduledDate)}</b>
                  <span>{PAYOUT_STATE[s.state] ?? ''}{s.funded
                    ? ` · останется ${formatMoney(s.bufferAfter)}`
                    : ''}</span></div>
                <div className="am num">{formatMoney(s.amount)}</div>
              </div>
            ))}
            {outlook.schedule.length === 0 && (
              <div className="note" style={{ marginTop: 0 }}>
                Пока режим стоит на паузе, новых выплат не назначено.
              </div>
            )}
          </div>

          <button className="btn" onClick={() => setMode('topup')}>Пополнить запас</button>
          {/*
            Деньги раньше срока — отдельным действием. Досрочной «выплатой»
            человек закрывал будущие даты одну за другой и вычерпывал запас
            на месяцы вперёд.
          */}
          {plan.status === 'active' && buffer > ZERO && (
            <button className="btn" onClick={() => setMode('take')}>Взять из запаса</button>
          )}
          <button className="btn" onClick={() => setMode('change')}>Изменить выплату</button>
          {plan.status === 'active'
            ? (
              <button className="btn" disabled={busy}
                onClick={async () => { setBusy(true); await pauseStablePlan(plan.id); flash('Стабильный бюджет на паузе'); await onSaved(); }}>
                Приостановить
              </button>
            )
            : (
              <button className="btn" disabled={busy}
                onClick={async () => { setBusy(true); await resumeStablePlan(plan.id); flash('Стабильный бюджет снова работает'); await onSaved(); }}>
                Возобновить
              </button>
            )}
          <button className="btn" onClick={() => setMode('stop')}>Отключить режим</button>
        </>
      )}

      {mode === 'topup' && (
        <TopUp state={state} planId={plan.id} onBack={() => setMode('view')}
          onFund={() => setMode('fund')} onSaved={onSaved} />
      )}
      {mode === 'fund' && (
        <FromEmergency state={state} planId={plan.id} onBack={() => setMode('view')}
          onSaved={onSaved} />
      )}
      {mode === 'change' && (
        <Change state={state} plan={plan} buffer={buffer} onBack={() => setMode('view')}
          onSaved={onSaved} />
      )}
      {mode === 'stop' && (
        <Stop plan={plan} buffer={buffer} onBack={() => setMode('view')} onSaved={onSaved} />
      )}
      {mode === 'take' && (
        <TakeEarly plan={plan} buffer={buffer} outlook={outlook}
          onBack={() => setMode('view')}
          onSaved={async () => { await onSaved(); onAllocate(); }} />
      )}
      {mode === 'reconcile' && outlook.gap && (
        <Reconcile plan={plan} buffer={buffer} gap={outlook.gap}
          onBack={() => setMode('view')} onSaved={onSaved} />
      )}
    </SheetWrap>
  );
}

/**
 * Взять деньги из запаса раньше срока.
 *
 * Отдельно от выплаты, и человеку это сказано прямо: график не сдвинется, а
 * ближайшая выплата может остаться без денег. Иначе «выплатить пораньше»
 * читается как безобидное, а на деле съедает будущие месяцы.
 */
function TakeEarly({ plan, buffer, outlook, onBack, onSaved }: {
  plan: StableBudgetPlan;
  buffer: Money;
  outlook: StableOutlook;
  onBack: () => void;
  onSaved: () => Promise<void> | void;
}) {
  const [amount, setAmount] = useState('');
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState('');
  const sum = parseAmountInput(amount);
  const left = clampPos(sub(buffer, sum));
  const next = outlook.schedule.find((x) => !x.decided);

  return (
    <div className="form">
      <div className="note" style={{ marginTop: 0 }}>В запасе {formatMoney(buffer)}.</div>
      <label>Сколько взять
        <Tip>Это не выплата по графику: даты не сдвинутся и выплаченными не
          станут.</Tip>
        <AmountInput value={amount} onChange={setAmount} placeholder="10 000" /></label>
      {sum > ZERO && (
        <div className={`note${next && left < plan.payout ? ' warn' : ''}`}>
          Останется {formatMoney(left)}.
          {next && left < plan.payout
            ? ` Выплаты ${dayMonthYear(next.scheduledDate)} на ${formatMoney(plan.payout)}`
              + ' тогда не хватит.'
            : ''}
        </div>
      )}
      {problem && <div className="note warn">{problem}</div>}
      <button className="btn pri" disabled={busy || sum <= ZERO}
        onClick={async () => {
          setBusy(true);
          const r = await takeFromBuffer({ planId: plan.id, amount: sum });
          setBusy(false);
          if (!r.done) { setProblem(r.reason ?? 'Не вышло'); return; }
          flash(`Взято из запаса ${formatMoney(sum)}`);
          await onSaved();
        }}>
        Взять {sum > ZERO ? formatMoney(sum) : ''}
      </button>
      <button className="btn" onClick={onBack}>Назад</button>
    </div>
  );
}

/**
 * Сверка после долгого отсутствия.
 *
 * Один вопрос вместо тринадцати. Варианты названы так, как человек о них
 * думает: «выплачивал», «не выплачивал», «начать заново» — и у каждого
 * прямо написано, что он сделает с деньгами.
 */
function Reconcile({ plan, buffer, gap, onBack, onSaved }: {
  plan: StableBudgetPlan;
  buffer: Money;
  gap: StableGap;
  onBack: () => void;
  onSaved: () => Promise<void> | void;
}) {
  const [actual, setActual] = useState(formatAmountInput(String(buffer / 100)));
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState('');
  const run = async (mode: 'paid' | 'skipped' | 'restart') => {
    setBusy(true);
    const r = await reconcileStable(mode === 'paid'
      ? { planId: plan.id, mode, bufferNow: parseAmountInput(actual) }
      : { planId: plan.id, mode });
    setBusy(false);
    if (!r.done) { setProblem(r.reason ?? 'Не вышло'); return; }
    flash(mode === 'paid' ? 'Запас сверен' : mode === 'skipped' ? 'Пропуски закрыты' : 'Выплаты — заново с сегодня');
    await onSaved();
  };

  return (
    <>
      <div className="note" style={{ marginTop: 0 }}>
        С {dayMonthYear(gap.from)} по {dayMonthYear(gap.to)} осталось
        {' '}{plural(gap.count, 'выплата', 'выплаты', 'выплат')} без ответа. Пока не
        разберём, срок следующей не считается — и деньги из запаса не уходят.
      </div>

      <div className="form">
        <label>Сколько сейчас в запасе на самом деле
          <Tip>Суммы по датам выдумывать не стану: их не знает никто. В отчёте
            останется изменение остатка запаса.</Tip>
          <AmountInput value={actual} onChange={setActual} placeholder="0" /></label>
      </div>
      {problem && <div className="note warn">{problem}</div>}
      <button className="btn pri" disabled={busy} onClick={() => run('paid')}>
        Выплачивал себе — сверить остаток
      </button>

      <button className="btn" disabled={busy} onClick={() => run('skipped')}>
        Не выплачивал — закрыть как пропуски
      </button>
      <div className="note" style={{ marginTop: 0 }}>
        Деньги в запасе остаются: {formatMoney(buffer)}.
      </div>

      <button className="btn" disabled={busy} onClick={() => run('restart')}>
        Начать заново с сегодня
      </button>
      <div className="note" style={{ marginTop: 0 }}>
        Прошлые выплаты останутся в истории, а пропущенный отрезок закроется
        без новых записей.
      </div>

      <button className="btn" onClick={onBack}>Назад</button>
    </>
  );
}

/** Предупреждения приходят заранее — за две выплаты, а не в день пустого счёта. */
function Warning({ outlook }: { outlook: StableOutlook }) {
  if (outlook.warning === 'none') return null;
  const text = outlook.warning === 'next_unfunded'
    ? 'Следующая выплата не обеспечена: денег в запасе меньше, чем выплата. '
      + 'Сама она не состоится и не уменьшится — реши, что делать.'
    : outlook.warning === 'one_left'
      ? 'В запасе осталась одна выплата. Дальше — пусто, если ничего не придёт.'
      : 'В запасе осталось две выплаты.';
  return (
    <section className="blk alarm">
      <div className="blkhead"><b>Запас кончается</b></div>
      <div className="note" style={{ marginTop: 0 }}>{text}</div>
      <div className="note">
        Что можно сделать: пополнить запас, уменьшить выплату, изменить
        периодичность, приостановить режим. Деньги из аварийной подушки —
        только отдельным решением.
      </div>
    </section>
  );
}

function TopUp({ state, planId, onBack, onFund, onSaved }: {
  state: AppState; planId: string; onBack: () => void; onFund: () => void;
  onSaved: () => Promise<void> | void;
}) {
  const [amount, setAmount] = useState('');
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState('');
  const sum = parseAmountInput(amount);
  const proposal = proposeAllocation(planInput(state));
  const free = clampPos(sub(proposal.liquid, proposal.reserved));

  return (
    <div className="form">
      <label>Сколько добавить в запас
        <AmountInput value={amount} onChange={setAmount} placeholder="50 000" /></label>
      <div className="note">Свободных денег сейчас {formatMoney(free)}.</div>
      {sum > free && (
        <div className="note warn">
          Столько свободных денег нет: остальное закреплено под платежи и цели.
        </div>
      )}
      {problem && <div className="note warn">{problem}</div>}
      <button className="btn pri" disabled={busy || sum <= ZERO || sum > free}
        onClick={async () => {
          setBusy(true);
          const result = await topUpBuffer(planId, sum);
          setBusy(false);
          if (!result.done) {
            setProblem(result.reason ?? 'Не получилось пополнить запас');
            return;
          }
          flash(`Запас пополнен на ${formatMoney(sum)}`);
          await onSaved();
        }}>
        Пополнить
      </button>
      <button className="btn" onClick={onFund}>Взять из аварийной подушки</button>
      <button className="btn" onClick={onBack}>Назад</button>
    </div>
  );
}

/**
 * Деньги из подушки — отдельным подтверждением и с показом последствий.
 *
 * Подушка собрана под потерю дохода и болезнь. Разрешить брать из неё на
 * ровное течение месяца можно, но человек обязан увидеть, чего лишается.
 */
function FromEmergency({ state, planId, onBack, onSaved }: {
  state: AppState; planId: string; onBack: () => void; onSaved: () => Promise<void> | void;
}) {
  const goal = emergencyFund(state.goals);
  const [amount, setAmount] = useState('');
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState('');
  const sum = parseAmountInput(amount);

  if (!goal) {
    return (
      <>
        <div className="note" style={{ marginTop: 0 }}>Аварийной подушки нет — брать неоткуда.</div>
        <button className="btn" onClick={onBack}>Назад</button>
      </>
    );
  }

  return (
    <div className="form">
      <div className="note" style={{ marginTop: 0 }}>
        В подушке {formatMoney(goal.currentAmount)} из {formatMoney(goal.targetAmount)}.
      </div>
      <label>Сколько перевести в запас
        <Tip>Подушка собрана на случай потери дохода и болезни — это другие деньги.</Tip>
        <AmountInput value={amount} onChange={setAmount} placeholder="30 000" /></label>
      {sum > ZERO && (
        <div className="note warn">
          После перевода в подушке останется {formatMoney(clampPos(sub(goal.currentAmount, sum)))}.
          Доходом это не станет: деньги просто сменят назначение.
        </div>
      )}
      {problem && <div className="note warn">{problem}</div>}
      <button className="btn pri" disabled={busy || sum <= ZERO}
        onClick={async () => {
          setBusy(true);
          const r = await moveFromEmergencyFund({ planId, goalId: goal.id, amount: sum });
          setBusy(false);
          if (!r.done) { setProblem(r.reason ?? 'Не вышло'); return; }
          flash(`Из «${goal.name}» в запас — ${formatMoney(sum)}`);
          await onSaved();
        }}>
        Да, взять из подушки
      </button>
      <button className="btn" onClick={onBack}>Не брать</button>
    </div>
  );
}

/** Изменение суммы и ритма: всегда «было → станет», всегда с последствиями. */
function Change({ state, plan, buffer, onBack, onSaved }: {
  state: AppState; plan: StableBudgetPlan; buffer: Money;
  onBack: () => void; onSaved: () => Promise<void> | void;
}) {
  const [payout, setPayout] = useState(formatAmountInput(String(plan.payout / 100)));
  const [cadence, setCadence] = useState<PayoutCadence>(plan.cadence);
  const [busy, setBusy] = useState(false);
  const sum = parseAmountInput(payout);

  const change = previewChange({
    today: state.today,
    plan,
    buffer,
    payouts: (state.stablePayouts ?? []).filter((x) => x.planId === plan.id),
    next: { payout: sum, cadence },
    obligations: state.obligations,
    reserves: state.reserves,
  });

  // Что станет с корзинами: тот же расчёт раскладки, но на изменённом плане.
  const after: AppState = {
    ...state,
    stablePlans: state.stablePlans.map(
      (p) => (p.id === plan.id ? { ...p, payout: sum, cadence } : p),
    ),
  };
  const p0 = proposeAllocation(planInput(state));
  const p1 = proposeAllocation(planInput(after));
  const goalsOf = (p: typeof p0) => add(...p.lines.goals.map((g) => g.amount));

  return (
    <div className="form">
      <label>Сумма выплаты
        <AmountInput value={payout} onChange={setPayout} placeholder="30 000" /></label>
      <div className="pick"><div className="k">Как часто</div>
        <div className="chips">
          {CADENCES.map((c) => (
            <button key={c} className={`chip${cadence === c ? ' on' : ''}`}
              onClick={() => setCadence(c)}>{cadenceTitle(c)}</button>
          ))}
        </div>
      </div>

      <div className="blk">
        <div className="blkhead"><b>Было → станет</b></div>
        <Rows rows={[
          ['Выплата', `${formatMoney(plan.payout)} → ${formatMoney(sum)}`],
          ['Как часто', `${cadenceTitle(plan.cadence)} → ${cadenceTitle(cadence)}`],
          ['Хватит выплат', `${change.was.payoutsLeft} → ${change.will.payoutsLeft}`],
          ['Запас кончится', `${change.was.exhaustsOn ? dayMonthYear(change.was.exhaustsOn) : '—'}`
            + ` → ${change.will.exhaustsOn ? dayMonthYear(change.will.exhaustsOn) : '—'}`],
          ['Повседневные нужды', `${formatMoney(p0.lines.need)} → ${formatMoney(p1.lines.need)}`],
          ['Свободные траты', `${formatMoney(p0.lines.want)} → ${formatMoney(p1.lines.want)}`],
          ['В цели', `${formatMoney(goalsOf(p0))} → ${formatMoney(goalsOf(p1))}`],
        ]} />
      </div>

      {change.atRisk.length > 0 && (
        <section className="blk alarm">
          <div className="blkhead"><b>Окажутся без денег
            <Tip>Это платежи после дня, когда запас кончится. Пока новых доходов
              нет, закрывать их будет нечем.</Tip></b></div>
          {change.atRisk.slice(0, 5).map((r) => (
            <div className="li" key={`${r.label}${r.dueDate}`}>
              <div className="nm"><b>{r.label}</b><span>{dayMonthYear(r.dueDate)}</span></div>
              <div className="am num">{formatMoney(r.amount)}</div>
            </div>
          ))}
        </section>
      )}

      <button className="btn pri" disabled={busy || sum <= ZERO}
        onClick={async () => {
          setBusy(true);
          await updateStablePlan(plan.id, { payout: sum, cadence });
          flash(`Выплата — ${formatMoney(sum)}`);
          await onSaved();
        }}>
        Применить
      </button>
      <button className="btn" onClick={onBack}>Оставить как есть</button>
    </div>
  );
}

/** Отключение: будущее отменяется, прошлое остаётся, резерв — по решению. */
function Stop({ plan, buffer, onBack, onSaved }: {
  plan: StableBudgetPlan; buffer: Money;
  onBack: () => void; onSaved: () => Promise<void> | void;
}) {
  const [busy, setBusy] = useState(false);
  return (
    <>
      <div className="note" style={{ marginTop: 0 }}>
        Отключение отменяет только будущие выплаты. История выплат и принятые
        раскладки остаются: это то, что уже случилось.
      </div>
      <div className="note">
        В запасе {formatMoney(buffer)} — они освободятся и вернутся в «не
        распределено». Доходом это не станет: деньги никуда не приходили.
      </div>
      <div className="note">
        Оставить их закреплёнными нельзя: выключенным режимом никто не
        управляет, и такой конверт просто запер бы деньги. Нужен запас без
        выплат — приостанови режим вместо отключения.
      </div>
      <button className="btn pri" disabled={busy}
        onClick={async () => { setBusy(true); await stopStablePlan(plan.id); flash('Стабильный бюджет выключен'); await onSaved(); }}>
        Отключить и освободить {formatMoney(buffer)}
      </button>
      <button className="btn" onClick={onBack}>Назад</button>
    </>
  );
}

/** Платежи после дня исчерпания: проверка, которую делают до согласия. */
function ObligationsAhead({ state, until }: { state: AppState; until: string | null }) {
  const soon = state.obligations.filter((o) => o.isActive).slice(0, 4);
  if (soon.length === 0) return null;
  return (
    <div className="blk">
      <div className="blkhead"><b>Обязательные платежи
        <Tip>{until
          ? 'Платежи после дня, когда запас кончится, — из других денег или из новых приходов.'
          : 'Платежи закрываются из тех же денег: выплата себе их не отменяет.'}</Tip></b></div>
      {soon.map((o) => (
        <div className="li" key={o.id}>
          <div className="nm"><b>{o.name}</b><span>{o.dayOfMonth} числа</span></div>
          <div className="am num">{formatMoney(o.amount)}</div>
        </div>
      ))}
      {until && (
        <div className="note">
          Если новых денег не будет, запас кончится {dayMonthYear(until)}.
        </div>
      )}
    </div>
  );
}

/** Строки «подпись — значение»; третьим элементом — пояснение к подписи. */
const Rows = ({ rows }: { rows: [string, string, ReactNode?][] }) => (
  <>
    {rows.map(([k, v, tip]) => (
      <div className="row" key={k}>
        <div className="rl">{k}{tip !== undefined && <Tip>{tip}</Tip>}</div>
        <div className="rv num">{v}</div>
      </div>
    ))}
  </>
);

/** Сколько лежит в буфере — для экранов, которым нужна только сумма. */
export const stableBuffer = (state: AppState): Money => {
  const s = stableSnapshot(state);
  return s ? bufferOf(state.reserves, s.plan.id) : ZERO;
};

/** Двухмесячный минимум для карточки предложения на главной. */
export const stableGap = (state: AppState): Money => stableReadiness({
  today: state.today,
  obligations: state.obligations,
  reserves: state.reserves,
  needBaseline: state.baselines.need,
  have: ZERO,
}).required;
